import { add, addHours, subHours, startOfDay } from 'date-fns';
import { getTimezoneOffset, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { ClinicUserClinicFragment, PimsIntegrationCapability } from 'shared/types/graphql';

type ClinicOfficeHoursType = {
  AutomationHours: 'AutomationHours';
  BusinessHours: 'BusinessHours';
  DirectBookingHours: 'DirectBookingHours';
};

type ClinicOfficeHour = {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  dayOfWeek: number;
  openAt: Date;
  closeAt: Date;
  isEnabled: boolean;
  isOpenTwentyFourHours: boolean;
  type: ClinicOfficeHoursType;
  clinicId: string | null;
};

export function copyTime(dateFrom: Date, timeFrom: Date, timezoneName = 'America/Chicago'): Date {
  const isUtc = getTimezoneOffset(timezoneName, new Date()) == 0;
  const dateOffset = getTimezoneOffset(timezoneName, dateFrom);
  const timeOffset = getTimezoneOffset(timezoneName, timeFrom);
  const offset = isUtc ? timeOffset - dateOffset : 0;
  return new Date(
    dateFrom.getFullYear(),
    dateFrom.getMonth(),
    dateFrom.getDate(),
    timeFrom.getHours(),
    timeFrom.getMinutes() + offset,
    timeFrom.getSeconds(),
    timeFrom.getMilliseconds(),
  );
}
export const lastSuccessfulSync = (
  capabilities:
    | {
        capability: string;
        lastSyncedAt: Date;
      }[]
    | undefined,
  timezoneName?: string | null,
): Date | null => {
  try {
    if (!capabilities || !capabilities.length) {
      return null;
    }

    const existingCapability = capabilities.find(
      ({ capability }) =>
        capability === PimsIntegrationCapability.ClinicPet ||
        capability === PimsIntegrationCapability.ClinicPetParent ||
        capability === PimsIntegrationCapability.Appointment ||
        capability === PimsIntegrationCapability.ClinicPetToClinicPetParent,
    );

    if (!existingCapability) {
      return null;
    }

    const lastFiveMinuteSync = new Date(existingCapability?.lastSyncedAt);
    const lastSync = convertTimeForDST(lastFiveMinuteSync, lastFiveMinuteSync, timezoneName ?? 'America/Chicago');

    return lastSync;
  } catch (error) {
    console.log(`timeFunc - LastSuccessfulSync Errored: ${error}`);
    return null;
  }
};

export const convertTimeForDST = (
  time: Date, // this is the timestamp being input (office hours or repeating block-offs may be in a different DST rule)
  date: Date, // this is the output date (based on DST rules on this output date)
  timezone = 'America/Chicago',
): Date => {
  const inLocalTime = utcToZonedTime(time, timezone);
  const hours = inLocalTime.getHours();
  const minutes = inLocalTime.getMinutes();
  const seconds = inLocalTime.getSeconds();

  // Now convert back to UTC
  return zonedTimeToUtc(add(startOfDay(date), { hours, minutes, seconds }), timezone);
};

export const checkIfClinicOpen = (
  timeToCheck: Date,
  officeHours: Pick<ClinicOfficeHour, 'openAt' | 'closeAt' | 'isOpenTwentyFourHours' | 'isEnabled'> | null,
  timezoneName: string,
  hoursPadding: number,
): boolean => {
  if (!officeHours?.openAt || !officeHours?.closeAt) {
    return true; // Default to "true," just in case
  }
  const officeHoursAccountingForDST = {
    ...officeHours,
    openAt: convertTimeForDST(officeHours?.openAt, new Date(), timezoneName),
    closeAt: convertTimeForDST(officeHours?.closeAt, new Date(), timezoneName),
  };

  // Some easy cases
  if (!officeHoursAccountingForDST?.isEnabled) {
    return false;
  }

  if (officeHoursAccountingForDST.isOpenTwentyFourHours) {
    return true;
  }

  // Is it in range now, is it in range 24 hours ago
  // if we hit midnight, we'll make another segment to check
  // The hard part is matching the opening day and the closing day
  const isOvernight = officeHoursAccountingForDST.openAt.getHours() > officeHoursAccountingForDST.closeAt.getHours();

  // We need to figure out the starting "day" to match it to
  const segments: [start: Date, end: Date][] = [];

  if (isOvernight) {
    // are we in an opening from yesterday
    const openAtYesterdayday = subHours(copyTime(timeToCheck, officeHoursAccountingForDST.openAt), 24);
    const closesAtYesterday = copyTime(timeToCheck, officeHoursAccountingForDST.closeAt);

    const openAtToday = copyTime(timeToCheck, officeHoursAccountingForDST.openAt);
    const closeAtTomorrow = addHours(copyTime(timeToCheck, officeHoursAccountingForDST.closeAt), 24);

    segments.push([subHours(openAtYesterdayday, hoursPadding), addHours(closesAtYesterday, hoursPadding)]);
    segments.push([subHours(openAtToday, hoursPadding), addHours(closeAtTomorrow, hoursPadding)]);
  } else {
    // No overnight segments to check
    const openAtToday = copyTime(timeToCheck, officeHoursAccountingForDST.openAt);
    const closeAtToday = copyTime(timeToCheck, officeHoursAccountingForDST.closeAt);
    segments.push([subHours(openAtToday, hoursPadding), addHours(closeAtToday, hoursPadding)]);
  }
  return segments.some(([start, end]) => {
    return start.getTime() <= timeToCheck.getTime() && timeToCheck.getTime() <= end.getTime();
  });
};

export const clinicWithinSyncWindow = (clinic: ClinicUserClinicFragment | null): boolean => {
  try {
    if (!clinic) {
      return true; // Always default to "true," just in case
    }
    const currentDate = new Date();
    const currentDay = currentDate.getDay();
    const currentClinicOfficeHour = clinic?.clinicOfficeHours.find(
      ({ dayOfWeek, type }) => dayOfWeek === currentDay && type === 'BusinessHours',
    );
    if (!currentClinicOfficeHour) {
      return true; // Default to "true" if, for whatever reason, we don't get any results here
    }

    const timezoneName = clinic.timezoneName ?? 'America/Chicago';

    const isClinicOpen = checkIfClinicOpen(currentDate, currentClinicOfficeHour, timezoneName, 1);

    return isClinicOpen;
  } catch (error) {
    console.log(error);
    // If there's an error determining the clinic's sync window, we'll default to syncing it
    return true;
  }
};
